import classnames from 'classnames';
import { isEqual, isNull, isUndefined, uniq } from 'lodash/fp';
import React, { useCallback, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../../../hooks/react-redux';
import { useSingleHiddenDocumentFields, useSingleHiddenFields } from '../../../../../../hooks/useHiddenSingleFields';
import { OpenFieldSection } from '../../../../../datasets/instances/store';
import { DatasetFieldType, DefinitionHierarchy, DocumentSpecificHiddenFields, HiddenFields, OpenFieldType, SingleDatasetField } from '../../../../../datasets/store';
import { Icon } from '../../../../../shared/icon/Icon';
import { CaretDown, CaretSide, SmileyPositive } from '../../../../../shared/icons';
import { Toggle } from '../../../../../shared/toggle';
import { CustomTooltip, InformationTooltip, OverflowTooltip } from '../../../../../shared/tooltip';
import { addDocumentReportField, DocumentReportField, getDefinitionHierarchy, getReportFields, getReportMetaFields, getSearchFieldSection, getSelectedDocumentNameIds, removeDocumentReportField } from '../../../store';
import { getDocumentReportField, getFlattenedFields, getIsSelectedMetaField } from '../../../store/utils';
import { getReportFormDatasetField } from '../../ReportDatasetFields';
import styles from '../../ReportDefinitions.module.scss';

const { green, lightGrey } = styles;

interface SingleFieldProps {
    field: SingleDatasetField;
    sectionId: string;
    width?: number;
    isLastFieldInGroup?: boolean;
    isLastFieldInSection?: boolean;
    groupIndex?: number;
    datasetOnlySection?: boolean;
    datasetId: number;
    parents: number;
    toggleSection: (fieldSection: OpenFieldSection) => void;
    getSectionOpen: (fieldSection: OpenFieldSection) => boolean;
    hiddenFields: HiddenFields;
    datasetHidden?: boolean;
    documentSpecificHiddenFields: DocumentSpecificHiddenFields;
    parentHiddenDocumentNameIds?: number[];
    parentFieldId: string;
}

export const SingleField: React.FC<SingleFieldProps> = ({
    field,
    width = 100,
    isLastFieldInGroup = false,
    isLastFieldInSection = false,
    sectionId,
    groupIndex,
    datasetOnlySection = false,
    datasetId,
    parents,
    toggleSection,
    getSectionOpen,
    hiddenFields,
    datasetHidden,
    documentSpecificHiddenFields,
    parentHiddenDocumentNameIds,
    parentFieldId
}) => {
    const dispatch = useAppDispatch();
    const reportDocumentNameIds = useAppSelector(getSelectedDocumentNameIds);
    const reportFields = useAppSelector(getReportFields);
    const searchFieldSection = useAppSelector(getSearchFieldSection);
    const selectedMetaFields = useAppSelector(getReportMetaFields);
    const definitionHierarchy = useAppSelector(getDefinitionHierarchy);

    const { label, description, refLabel, settings, type, id } = field;
    const wysiwygFieldOpen = useMemo(() => !isUndefined(settings.isCollapsible) && !settings.isCollapsible, [settings.isCollapsible]);
    const openField = useMemo(() => ({ sectionId, datasetId: datasetId.toString() || '', fieldId: id!, type: OpenFieldType.FIELD, groupIndex, parentFieldId }), [groupIndex, datasetId, id, sectionId, parentFieldId]);
    const fieldOpen = useMemo(() => getSectionOpen(openField) || datasetOnlySection || wysiwygFieldOpen, [getSectionOpen, openField, datasetOnlySection, wysiwygFieldOpen]);
    const fieldOpenIcon = fieldOpen ? CaretDown : CaretSide;

    const isCollapsible = settings.isCollapsible || [DatasetFieldType.DATASET, DatasetFieldType.CALCULATOR].includes(type);
    const showFieldLabel = type !== DatasetFieldType.LABEL && !datasetOnlySection;
    const isLabelField = type === DatasetFieldType.LABEL;

    const currentHiddenDatasetFields = useMemo(() => hiddenFields[datasetId] || [], [datasetId, hiddenFields]);

    const hiddenSingleFields = useSingleHiddenFields(currentHiddenDatasetFields);

    const sectionIsHidden = useMemo(() => !!currentHiddenDatasetFields.filter(({ type }) => type === OpenFieldType.SECTION).find(field => field.sectionId === sectionId), [currentHiddenDatasetFields, sectionId]);

    const fieldHidden = useMemo(() => sectionIsHidden || datasetHidden || !!hiddenSingleFields.find(({ fieldId }) => fieldId === id), [hiddenSingleFields, id, sectionIsHidden, datasetHidden]);

    const documentHiddenFields = useMemo(() => documentSpecificHiddenFields[datasetId] || [], [datasetId, documentSpecificHiddenFields]);
    const documentHiddenSingleFields = useSingleHiddenDocumentFields(documentHiddenFields);
    const documentsHiddenForSection = useMemo(() => documentHiddenFields.filter(({ type }) => type === OpenFieldType.SECTION).find(hiddenField => hiddenField.sectionId === sectionId)?.documentNameIds || [], [documentHiddenFields, sectionId]);
    const documentsHiddenForField = useMemo(() => documentHiddenSingleFields.find(({ fieldId }) => fieldId === id)?.documentNameIds || [], [documentHiddenSingleFields, id]);
    const parentHiddenDocuments = !isUndefined(parentHiddenDocumentNameIds) ? parentHiddenDocumentNameIds : [];
    const allDocumentsHiddenForField = uniq([...documentsHiddenForField, ...documentsHiddenForSection, ...parentHiddenDocuments]);

    const fieldHiddenForAllDatasetDocuments = useMemo(() => documentsHiddenForField.length > 0 && reportDocumentNameIds.every(documentNameId => documentsHiddenForField.includes(parseInt(documentNameId))), [reportDocumentNameIds, documentsHiddenForField]);
    const sectionHiddenForAllDatasetDocuments = useMemo(() => documentsHiddenForSection.length > 0 && reportDocumentNameIds.every(documentNameId => documentsHiddenForSection.includes(parseInt(documentNameId))), [reportDocumentNameIds, documentsHiddenForSection]);

    const isSearchedField = useMemo(() => !isNull(searchFieldSection) && searchFieldSection.type === OpenFieldType.FIELD && searchFieldSection.fieldId === id && !isUndefined(searchFieldSection.sectionId) && searchFieldSection.sectionId === sectionId, [searchFieldSection, id, sectionId]);

    const isMetaFieldType = useMemo(() => type === DatasetFieldType.DOCUMENT, [type]);

    const toggleDisabledTooltip = useMemo(() => {
        if (isMetaFieldType) {
            return ['This field can be added in the meta document fields tab'];
        }
        return null;
    }, [isMetaFieldType]);

    const getDefinitionInHierarchy = useCallback((datasetId: number, parentFieldId: string) => definitionHierarchy.find(({ fieldId, definition }) => isEqual({ parentFieldId: fieldId, datasetId: definition.datasetId! }, { parentFieldId, datasetId })) || null, [definitionHierarchy]);

    const definitionInHierarchy = useMemo(() => {
        if (type !== DatasetFieldType.DATASET) {
            return getDefinitionInHierarchy(datasetId, parentFieldId);
        }
        const childDatasetId = parseInt(settings.datasetLinked!);
        const childParentFieldId = id!;
        return getDefinitionInHierarchy(childDatasetId, childParentFieldId);
    }, [type, datasetId, parentFieldId, settings, getDefinitionInHierarchy, id]);

    const childFields = useMemo(() => {
        if (type === DatasetFieldType.DATASET) {
            if (!isNull(definitionInHierarchy)) {
                return getFlattenedFields(definitionInHierarchy.definition);
            }
        }
        return [field];
    }, [definitionInHierarchy, type, field]);

    const checkIsSelectedMetaField = useCallback((field: SingleDatasetField) => field.type === DatasetFieldType.DOCUMENT && !isNull(field.settings.documentProperty) && !isUndefined(field.settings.documentProperty) && getIsSelectedMetaField(field.settings.documentProperty, selectedMetaFields), [selectedMetaFields]);
    const checkIsIncludedReportField = useCallback((documentReportField: DocumentReportField) => !isUndefined(reportFields.find(reportField => isEqual(documentReportField, reportField))), [reportFields]);

    const isIncludedField = useCallback((definitionInHierarchy: DefinitionHierarchy, field: SingleDatasetField) => {
        const isIncludedReportField = checkIsIncludedReportField(getDocumentReportField(definitionInHierarchy, field, sectionId));
        const isSelectedMetaField = checkIsSelectedMetaField(field);
        return isIncludedReportField || isSelectedMetaField;
    }, [checkIsSelectedMetaField, checkIsIncludedReportField, sectionId]);

    const isIncludedInReport = useMemo(() => !isNull(definitionInHierarchy) && childFields.every(field => isIncludedField(definitionInHierarchy, field)), [definitionInHierarchy, childFields, isIncludedField]);

    const addReportField = useCallback(() => !isNull(definitionInHierarchy) && childFields.filter(field => !isIncludedField(definitionInHierarchy, field)).forEach(field => {
        if (field.type !== DatasetFieldType.DOCUMENT) {
            const documentReportField = getDocumentReportField(definitionInHierarchy, field, sectionId);
            dispatch(addDocumentReportField(documentReportField));
        }
    }), [dispatch, definitionInHierarchy, childFields, sectionId, isIncludedField]);

    const removeReportField = useCallback(() => childFields.forEach(field => {
        if (field.type !== DatasetFieldType.DOCUMENT) {
            dispatch(removeDocumentReportField(field.id!));
        }
    }), [dispatch, childFields]);

    const toggleReportField = useCallback((checked: boolean, e: MouseEvent | React.SyntheticEvent<MouseEvent | KeyboardEvent, Event>) => {
        e.stopPropagation();
        checked ? addReportField() : removeReportField();
    }, [removeReportField, addReportField]);

    if (fieldHidden || fieldHiddenForAllDatasetDocuments || sectionHiddenForAllDatasetDocuments) {
        return null;
    }

    return (
        <div
            className={classnames(styles.singleFieldWrapper, { [styles.lastFieldInGroup]: isLastFieldInGroup, [styles.isLabelField]: isLabelField && !isLastFieldInSection, [styles.searchedFieldWrapper]: isSearchedField })}
            style={{ width: `${width}%` }}
            data-testid={`report-form-field-${id}-wrapper`}
            id={`document-report-field-${id}`}
        >
            {showFieldLabel &&
                <div className={classnames(styles.fieldTitleWrapper, { [styles.fieldCanCollapse]: isCollapsible })}>
                    {isCollapsible &&
                        <div className={styles.fieldOpenWrapper} onClick={() => toggleSection(openField)} data-testid='collapsible-field'>
                            <Icon icon={fieldOpenIcon} fontSize={15} />
                        </div>
                    }
                    <OverflowTooltip
                        className={classnames(styles.fieldLabel, {
                            [styles.emptyFieldLabel]: !label,
                            [styles.datasetSectionText]: type === DatasetFieldType.DATASET
                        })}
                        overlayText={label || 'Label...'}
                        testId={`form-field-${id}-label`}
                        onClick={() => isCollapsible && toggleSection(openField)}
                    />
                    {description && <InformationTooltip content={description} />}
                    {refLabel && settings.showRef && <InformationTooltip content={refLabel} label='?' />}
                    {settings.riskField && <CustomTooltip overlayText='Risk Tolerance is available on this field'><div className={styles.riskFieldWrapper}><Icon icon={SmileyPositive} fill={isIncludedInReport ? green : lightGrey} fontSize={22} /></div></CustomTooltip>}
                    <Toggle checked={isIncludedInReport} onChange={toggleReportField} disabled={isMetaFieldType} onColour={green} disabledTooltip={toggleDisabledTooltip} />
                </div>
            }
            {getReportFormDatasetField(
                field,
                datasetId,
                fieldOpen,
                parents,
                toggleSection,
                getSectionOpen,
                sectionId,
                groupIndex,
                fieldHidden,
                allDocumentsHiddenForField
            )}
        </div>
    );
};
